import { SUPPORTED_NETWORKS } from '../../../../constants/socialMedia';

/**
 * Performance module constants — raw values only. Higher-level display config
 * (titles, static copy) lives in performanceConfig.js.
 */

export const TIMELINE_RANGE_OPTIONS = [
  { label: '7D', value: 7 },
  { label: '14D', value: 14 },
  { label: '30D', value: 30 },
];

// `?days=` values as they appear in the URL.
export const TIMELINE_DAY_VALUES = TIMELINE_RANGE_OPTIONS.map((option) => String(option.value));

export const DEFAULT_TIMELINE_RANGE = 14;

// The backend rejects a custom `?from=&to=` window of a year or more.
export const MAX_RANGE_DAYS = 365;

export const KPI_LABEL_ORDER = [
  'earned_media_value',
  'return_per_dollar',
  'engagement_rate',
  'reach',
];

export const PERFORMANCE_TAB_KEYS = {
  CAMPAIGN_RESPONSE: 'campaign-response',
  PLATFORM_DEEP_DIVE: 'platform-deep-dive',
};

// Full-page "View all" lists, routed as siblings of the tabs
// (`…/performance/all-creators`, `…/performance/all-content`).
export const PERFORMANCE_PAGE_KEYS = {
  ALL_CREATORS: 'all-creators',
  ALL_CONTENT: 'all-content',
};

// The list pages' own query params, kept apart from the tabs' `?network=&days=` so
// both survive the trip to a list and back. `fromTab` is the tab to return to (not
// `from`, which is the deep dive's custom date range).
export const LIST_PAGE_PARAMS = {
  PLATFORM: 'platform',
  SIZE: 'size',
  PAGE: 'page',
  PER_PAGE: 'perPage',
  FROM_TAB: 'fromTab',
};

export const LIST_PAGE_SIZE_OPTIONS = [10, 25, 50];

// Backend creator tiers (`creatorSize`), largest first.
export const CREATOR_SIZE_KEYS = ['mega', 'macro', 'mid', 'micro', 'nano'];

// Platform switch on Campaign response — filters Top Creators + What's Converting's
// Top Content via `?network=` (both endpoints support it per the AAPU-6574 contract;
// /performance and /timeline don't, so KPI tiles and the Daily EMV chart stay
// aggregate regardless of this selection). Not in the Campaign response Figma
// frames — confirmed with Aldrin it's needed there too, design just missed it.
// Derived from the SOCIAL_NETWORKS registry's own `supported` flag (not a
// separately hand-maintained list) — a network becomes filterable here the
// moment it's marked supported in the registry, no second edit required.
// Display labels also come from that registry (performanceConfig.js). Includes
// LinkedIn: the backend has accepted it on these endpoints since AAPU-6853.
export const NETWORK_FILTER_KEYS = SUPPORTED_NETWORKS;

// Platform switch on Platform deep dive — the AAPU-6853 deep-dive endpoints accept
// every supported network. Same list as Campaign response's today.
export const DEEP_DIVE_NETWORK_KEYS = SUPPORTED_NETWORKS;

// Networks the backend stores no view counts for (AAPU-6853 PlatformMetricLabels),
// so ranking by views returns nothing — rank posts/hashtags by engagements instead.
export const NETWORKS_WITHOUT_VIEWS = ['linkedin'];

export const LINKEDIN_NETWORK = 'linkedin';

// The Platform switch's "All" option (AAPU-7109): every network at once. It's a
// switch value only, never a platform — the endpoints read a missing `network` as
// every network, so performanceEndpoint drops it from the query.
export const ALL_NETWORKS_KEY = 'all';

// The one network the TikTok-only figures (reach, watch time, completion, the
// organic/sponsored split) come from — flagged as such under "All".
export const TIKTOK_NETWORK = 'tiktok';

export const DEFAULT_NETWORK = ALL_NETWORKS_KEY;

export const GENERIC_ERROR_MESSAGE = 'Something went wrong';

// Stable DOM ids for each section/card header, used as scroll-anchor targets for
// imperative in-page navigation (getElementById + scrollIntoView). Page-global, so
// namespaced with a `performance-` prefix; a section nav references these same keys.
export const PERFORMANCE_ANCHOR_IDS = {
  WHATS_WORKING: 'performance-whats-working',
  FIELD_INTELLIGENCE: 'performance-field-intelligence',
  SENTIMENT: 'performance-sentiment',
  DAILY_EMV: 'performance-daily-emv',
  TOP_CREATORS: 'performance-top-creators',
  WHATS_CONVERTING: 'performance-whats-converting',
  SHARE_OF_VOICE: 'performance-share-of-voice',
  NET_SENTIMENT: 'performance-net-sentiment',
  // Platform deep dive
  DEEP_DIVE_KPIS: 'performance-deep-dive-kpis',
  DEEP_DIVE_VIEWS: 'performance-deep-dive-views',
  DEEP_DIVE_QUALITY: 'performance-deep-dive-quality',
  DEEP_DIVE_VIDEOS: 'performance-deep-dive-videos',
  DEEP_DIVE_HASHTAGS: 'performance-deep-dive-hashtags',
};

// `perPage` for the fixed-size lists, matching the Figma frames' row counts. The
// skeletons below reuse them so a loading list is the same height as a loaded one.
export const LIST_PER_PAGE = {
  TOP_CREATORS: 5,
  TOP_CONTENT: 3,
  DEEP_DIVE_TOP_CREATORS: 3,
  BEST_VIDEOS: 5,
};

// Row/bar counts for skeleton placeholders, matching Figma's density.
export const SKELETON_ROW_COUNTS = {
  TOP_CONTENT: LIST_PER_PAGE.TOP_CONTENT,
  SHARE_OF_VOICE: 6,
  NET_SENTIMENT_EVENTS: 5,
  TOP_CREATORS: LIST_PER_PAGE.TOP_CREATORS,
  // Platform deep dive — match the hook's perPage / the backend's max of 5 hashtags.
  DEEP_DIVE_TOP_CREATORS: LIST_PER_PAGE.DEEP_DIVE_TOP_CREATORS,
  BEST_VIDEOS: LIST_PER_PAGE.BEST_VIDEOS,
  FORMATS: 4,
  HASHTAGS: 5,
  RISING_HASHTAGS: 4,
};
